import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewEncapsulation,
} from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { DeviceConfigService } from "@obg/ngx.global";
import { coerceBoolean } from "@obg/ngx.global.evo";
import { Subscription } from "rxjs";

import { ElementType, Option } from "./select.model";

@Component({
	selector: "obg-m-select",
	templateUrl: "./select.component.html",
	styleUrls: ["./select.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class SelectComponent<T> implements OnInit, OnChanges, OnDestroy {
	@HostBinding("class.obg-m-select") hostCssClass = true;

	@Input() options: Option<T>[] = [];
	@Input() placeholder: string | undefined;
	@Input() control: UntypedFormControl | undefined;
	@Input() appearance: string | undefined;

	@Input()
	@coerceBoolean()
	// eslint-disable-next-line @angular-eslint/no-input-prefix
	isDisabled = false;

	/**
	 * Enable mobile v2 view.
	 * Instead of using native select and option we will be using mat-select and mat-option.
	 * This fixes the issue of safari and ios devices showing empty first option in select element.
	 */
	@Input()
	@coerceBoolean()
	// eslint-disable-next-line @angular-eslint/no-input-prefix
	isMobileV2Enabled = false;

	@Output() openedChange: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() valueChange: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() keydownTab: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() keyupTab: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() keydownShiftTab: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() keyupShiftTab: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() focused: EventEmitter<boolean> = new EventEmitter<boolean>();

	isRequired = false;
	isDesktop = true;
	elementType: ElementType | undefined;

	private control$$: Subscription | undefined;

	constructor(deviceConfigService: DeviceConfigService) {
		this.isDesktop = deviceConfigService.isDesktop();
	}

	ngOnInit() {
		this.determineElementType();
		this.isValidationResultRequired();
		this.setControlDisabledState();
		this.subscribeToControlChanges();
	}

	ngOnChanges(changes: SimpleChanges): void {
		this.isDisabled = changes["isDisabled"] || this.control?.status === "DISABLED" ? true : false;
		this.setControlDisabledState();
	}

	ngOnDestroy() {
		if (this.control$$) {
			this.control$$.unsubscribe();
		}
	}

	trackByValue(_index: number, item: Option<T>): T {
		return item.value;
	}

	private setControlDisabledState(): void {
		const emitOptions = { emitEvent: false };

		this.isDisabled ? this.control?.disable(emitOptions) : this.control?.enable(emitOptions);
	}

	private isValidationResultRequired() {
		if (!this.control || !this.control.validator) {
			return;
		}

		const validationResult = this.control.validator(new UntypedFormControl());
		this.isRequired = validationResult !== null && validationResult.required;
	}

	private determineElementType(): void {
		if (this.isDesktop) {
			this.elementType = "desktop";

			return;
		}

		if (this.isMobileV2Enabled) {
			this.elementType = "mobileV2";

			return;
		}

		this.elementType = "mobile";
	}

	private subscribeToControlChanges(): void {
		if (!this.control) return;

		this.control$$ = this.control.valueChanges.subscribe(() => {
			this.isDisabled = this.control?.status === "DISABLED" ? true : false;
			this.setControlDisabledState();
		});
	}
}
